/**
 * Hub Probe — オフライン → オンライン復帰のためのリトライループ。
 *
 * `hubStatus` と `navigator.onLine` を監視し、Hub が offline の間
 * 指数バックオフで periodic な再接続プローブを実行する。online に
 * 戻ったらタイマーを止め、復帰時のリスナー（キャッシュ無効化等）を発火する。
 *
 * Sweeper `hubProbe.ts` を nawabari に合わせてスリム化したもの。
 *
 * 設計方針:
 *   - hubStatus.ts は依存なしで保ち、こちらに retry ロジックを集約。
 *   - 実際のプローブは `getYmgSdk(forceRetry: true)` 相当の関数を渡してもらう。
 *   - 「Reconnect」ボタン押下時の即時プローブは `triggerProbeNow()` で実行。
 *
 * バックオフ:
 *   - 初回: offline 遷移後 5 秒
 *   - 以降: 倍増（最大 60 秒）
 *   - online 遷移時に 5 秒へリセット
 */

import {
  getHubStatus,
  setHubStatus,
  subscribeHubStatus,
  type HubStatus,
} from './hubStatus'

const INITIAL_DELAY_MS = 5_000
const MAX_DELAY_MS = 60_000

type ProbeFn = () => Promise<boolean>

let timerId: ReturnType<typeof setTimeout> | null = null
let unsubscribeStatus: (() => void) | null = null
let removeOnlineListener: (() => void) | null = null
let probing = false
let currentDelay = INITIAL_DELAY_MS
let probeFn: ProbeFn | null = null
let started = false

type RecoveryListener = () => void | Promise<void>
const onRecoveryListeners = new Set<RecoveryListener>()

function clearTimer(): void {
  if (timerId !== null) {
    clearTimeout(timerId)
    timerId = null
  }
}

function scheduleNextProbe(): void {
  clearTimer()
  if (getHubStatus() === 'online') return
  timerId = setTimeout(() => {
    void runProbe()
  }, currentDelay)
}

async function runProbe(): Promise<void> {
  if (probing) return
  if (getHubStatus() === 'online') return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    // OS レベルでネットワーク断 → タイマーを止めて 'online' イベント待ち。
    // （定期プローブを撃ち続けても無駄なリソース消費になる）
    clearTimer()
    return
  }
  if (!probeFn) {
    console.warn('[hubProbe] no probe function registered')
    return
  }
  probing = true
  try {
    console.log(`[hubProbe] probing (delay was ${currentDelay}ms)`)
    const success = await probeFn()
    if (success) {
      currentDelay = INITIAL_DELAY_MS
      if (getHubStatus() !== 'online') {
        setHubStatus('online')
      }
    } else {
      currentDelay = Math.min(currentDelay * 2, MAX_DELAY_MS)
      if (getHubStatus() !== 'offline') {
        setHubStatus('offline')
      }
    }
  } catch (err) {
    console.warn('[hubProbe] probe threw', err)
    currentDelay = Math.min(currentDelay * 2, MAX_DELAY_MS)
    if (getHubStatus() !== 'offline') {
      setHubStatus('offline')
    }
  } finally {
    probing = false
    scheduleNextProbe()
  }
}

function handleStatusChange(status: HubStatus): void {
  if (status === 'online') {
    clearTimer()
    currentDelay = INITIAL_DELAY_MS
    // 復旧リスナーを 1 回だけ発火。listener の追加/削除に対する再入を避けるため snapshot。
    const snapshot = Array.from(onRecoveryListeners)
    for (const listener of snapshot) {
      try {
        const result = listener()
        if (result && typeof (result as Promise<void>).catch === 'function') {
          ;(result as Promise<void>).catch((err) => {
            console.warn('[hubProbe] recovery listener rejected', err)
          })
        }
      } catch (err) {
        console.warn('[hubProbe] recovery listener threw', err)
      }
    }
    return
  }
  // offline / unknown → 次のプローブをスケジュール
  scheduleNextProbe()
}

function handleNavigatorOnline(): void {
  console.log('[hubProbe] navigator.online event — probing immediately')
  currentDelay = INITIAL_DELAY_MS
  void runProbe()
}

// ─── Public API ────────────────────────────────────────────────────────────

/** 起動時に呼ぶ。冪等。`probe` は SDK 再 init を試みて成功なら true を返す関数。 */
export function startHubProbe(probe: ProbeFn): void {
  probeFn = probe
  if (started) return
  started = true
  unsubscribeStatus = subscribeHubStatus(handleStatusChange)
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleNavigatorOnline)
    removeOnlineListener = () => window.removeEventListener('online', handleNavigatorOnline)
  }
  if (getHubStatus() !== 'online') {
    scheduleNextProbe()
  }
}

export function stopHubProbe(): void {
  started = false
  clearTimer()
  if (unsubscribeStatus) {
    unsubscribeStatus()
    unsubscribeStatus = null
  }
  if (removeOnlineListener) {
    removeOnlineListener()
    removeOnlineListener = null
  }
  probing = false
  currentDelay = INITIAL_DELAY_MS
}

/**
 * バックオフを無視して即時プローブする。OfflineBanner の「再接続」ボタンから使用。
 */
export async function triggerProbeNow(): Promise<boolean> {
  if (!probeFn) {
    console.warn('[hubProbe] triggerProbeNow called before startHubProbe')
    return false
  }
  clearTimer()
  if (probing) {
    // 進行中のプローブを 5 秒待ってから状態を返す
    for (let i = 0; i < 50; i++) {
      await new Promise((r) => setTimeout(r, 100))
      if (!probing) break
    }
    return getHubStatus() === 'online'
  }
  await runProbe()
  return getHubStatus() === 'online'
}

/** offline → online 遷移時に発火するリスナー登録。leaderboard キャッシュ無効化等に使う。 */
export function onHubRecovery(listener: RecoveryListener): () => void {
  onRecoveryListeners.add(listener)
  return () => {
    onRecoveryListeners.delete(listener)
  }
}
